'use client';

import type {
  OrgChatAttachmentInput,
  OrgChatLifecycleStatus,
  OrgChatPresetDataInput,
  OrgThreadPendingSteerSummary,
  OrgThreadSummary,
  QueuedMessageSummary,
} from '@/components/org-chat/org-chat-types';
import type { Id } from '@/convex/_generated/dataModel';
import type {
  ArtifactRunCancelResult,
  ArtifactRunDispatchReceipt,
  ArtifactRunState,
} from '@/components/org-chat/artifact-run-controller';
import type { ArtifactPanelFollowUpOptions } from '@/components/org-chat/org-chat-types';
import { createContext, ReactNode, useContext } from 'react';

export type { OrgThreadSummary } from '@/components/org-chat/org-chat-types';

export type OrgChatSendOptions = {
  clientInputId?: string;
  /** Submit-event time, retained across preparation, navigation and retries. */
  clientSendStartedAt?: number;
  realtimeVoiceSessionId?: string;
  /** Only the composer's own submit sets this: the human typed the text. */
  typedByUser?: boolean;
  voiceMode?: boolean;
  lastAssistantText?: string;
  audioBase64?: string;
  forceDeepResearch?: boolean;
  targetThreadRouteRef?: string | null;
  targetThreadRouteRefs?: Array<string>;
  newThreadCount?: number;
  /** Stable route identities retained when retrying failed thread creation. */
  newThreadRouteRefs?: Array<string>;
  /**
   * Upload ids of files still uploading that this first message takes with
   * it (#4844). Only the new-task navigation send honours it.
   */
  pendingUploadIds?: Array<string>;
};

export type OrgChatMessageDeliveryStatus = 'queued' | 'started';

export type OrgChatMessageDeliveryResult = {
  status: OrgChatMessageDeliveryStatus;
  accepted: boolean;
  targets?: Array<{ routeRef: string; accepted: boolean; error?: string }>;
  retryOptions?: OrgChatSendOptions;
};

export type OrgChatInterfaceContextValue = {
  focusInput: () => void;
  sendMessage: (
    prompt: string,
    attachments?: Array<OrgChatAttachmentInput>,
    presetData?: OrgChatPresetDataInput,
    userPrompt?: string,
    options?: OrgChatSendOptions
  ) => Promise<OrgChatMessageDeliveryResult>;
  dispatchMessage?: (
    prompt: string,
    attachments?: Array<OrgChatAttachmentInput>,
    presetData?: OrgChatPresetDataInput,
    userPrompt?: string,
    options?: OrgChatSendOptions
  ) => Promise<OrgChatMessageDeliveryResult>;
  sendMessageNow?: (
    prompt: string,
    attachments?: Array<OrgChatAttachmentInput>,
    presetData?: OrgChatPresetDataInput,
    userPrompt?: string,
    options?: OrgChatSendOptions
  ) => Promise<OrgChatMessageDeliveryResult>;
  /** Open the live voice call overlay for the current org thread. */
  openVoiceCall?: () => void;
  /** Close the live voice call if it is currently open. */
  closeVoiceCall?: () => void;
  /** Whether the live voice call UI is currently active. */
  isVoiceCallOpen?: boolean;
  addAttachmentsToInput?: (
    attachments: Array<OrgChatAttachmentInput>,
    options?: { prompt?: string }
  ) => void;
  enqueueMessage?: (
    prompt: string,
    attachments?: Array<OrgChatAttachmentInput>,
    presetData?: OrgChatPresetDataInput,
    userPrompt?: string,
    queueId?: string,
    options?: OrgChatSendOptions
  ) => Promise<void>;
  steerMessage?: (
    prompt: string,
    attachments?: Array<OrgChatAttachmentInput>,
    presetData?: OrgChatPresetDataInput,
    userPrompt?: string,
    clientInputId?: string
  ) => Promise<void>;
  stopStreaming?: () => Promise<boolean>;
  dispatchArtifactRun?: (
    prompt: string,
    options?: ArtifactPanelFollowUpOptions
  ) => Promise<ArtifactRunDispatchReceipt>;
  cancelArtifactRun?: (expected: {
    promptMessageId: string;
    workflowId: string;
  }) => Promise<ArtifactRunCancelResult>;
  beginOptimisticQuestionResume?: () => void;
  cancelOptimisticQuestionResume?: () => void;
  pending: boolean;
  /** True while the parent turn or any thread-owned background work can stop. */
  cancellableWorkActive?: boolean;
  agentRunState?: ArtifactRunState;
  canAttemptSteerShortcut?: boolean;
  status: OrgChatLifecycleStatus;
  errorMessage?: string | null;
  retrying?: boolean;
  deepResearchEnabled?: boolean;
  orgSlug: string;
  orgThread: OrgThreadSummary;
  workspacePanelTargetId?: string;
  workspacePanelPath?: string | null;
  workspacePanelOpenPaths?: string[];
  registerWorkspacePanelTab?: (path: string) => void;
  openWorkspacePanel?: (
    path: string,
    options?: {
      displayMode?: 'panel' | 'expanded' | 'fullscreen';
      source?: 'auto' | 'user';
      openPaths?: string[];
    }
  ) => void;
  closeWorkspacePanel?: () => void;
  closeWorkspacePanelPath?: (path?: string | null) => void;
  closeAllWorkspacePanelTabs?: () => void;
  setWorkspacePanelTabs?: (
    paths: string[],
    options?: {
      activePath?: string | null;
      displayMode?: 'panel' | 'expanded' | 'fullscreen';
      source?: 'auto' | 'user';
    }
  ) => void;
  activateWorkspacePanelPath?: (
    path: string,
    options?: {
      displayMode?: 'panel' | 'expanded' | 'fullscreen';
      source?: 'auto' | 'user';
      openPaths?: string[];
    }
  ) => void;
  shouldAutoOpenWorkspacePanel?: (autoOpenKey: string) => boolean;
  isSendAllowed: boolean;
  // Compatibility alias during migration.
  canSend: boolean;
  /** The thread route is mounted but its thread document is still loading. */
  isThreadLoading?: boolean;
  liveViewers: Array<{
    _id: Id<'users'>;
    name?: string | null;
    email?: string | null;
    image?: string | null;
    imageStorageKey?: string | null;
  }>;
  queuedMessages?: Array<QueuedMessageSummary>;
  pendingSteers?: Array<OrgThreadPendingSteerSummary>;
  deleteQueuedMessage?: (queueId: string) => Promise<void>;
  updateQueuedMessage?: (queueId: string, prompt: string) => Promise<void>;
  replaceQueuedMessage?: (
    queueId: string,
    prompt: string,
    attachments?: Array<OrgChatAttachmentInput>,
    presetData?: OrgChatPresetDataInput,
    rawUserPrompt?: string
  ) => Promise<void>;
  reorderQueuedMessages?: (queueIds: Array<string>) => Promise<void>;
  sendQueuedMessageNow?: (queueId: string) => Promise<void>;
};

const OrgChatInterfaceContext = createContext<
  OrgChatInterfaceContextValue | undefined
>(undefined);

export function OrgChatInterfaceProvider({
  value,
  children,
}: {
  value: OrgChatInterfaceContextValue;
  children: ReactNode;
}) {
  return (
    <OrgChatInterfaceContext.Provider value={value}>
      {children}
    </OrgChatInterfaceContext.Provider>
  );
}

export function useOrgChatInterface(): OrgChatInterfaceContextValue {
  const ctx = useContext(OrgChatInterfaceContext);
  if (!ctx) {
    throw new Error(
      'useOrgChatInterface must be used within an OrgChatInterfaceProvider'
    );
  }
  return ctx;
}

export function useOrgChatInterfaceOptional():
  | OrgChatInterfaceContextValue
  | undefined {
  return useContext(OrgChatInterfaceContext);
}
